import interpolateComponents from '@automattic/interpolate-components';
import { ReactElement, ReactNode, useCallback } from 'react';
import { sprintf } from 'sprintf-js';

import { Text } from 'components/Text/Text';
import { fontWeights } from 'theme';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { MappedLocalNotification } from 'utils/localNotifications';

type Props = {
  notification: MappedNotification | MappedLocalNotification;
  render: (args: {
    to: string;
    eventName: string;
    eventProperties: Record<string, unknown>;
    children: ReactNode;
  }) => ReactElement;
};

export function NotificationContent({ notification, render }: Props) {
  const truncateTitle = useCallback(
    (title: string) =>
      title.length > 70 ? title.slice(0, 70).concat('...') : title,
    [],
  );

  switch (notification.type) {
    case 'NEW_APPLICATIONS_BY_LISTING':
      return render({
        to: `/admin/${
          notification.data.orgId
        }/${notification.data.type.toLowerCase()}s/${notification.data.id}`,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.type,
          listing_id: notification.data.id,
          listing_type: notification.data.type,
          listing_name: notification.data.name,
          org_id: notification.data.orgId,
          application_count: notification.data.applications.length,
        },
        children: (
          <>
            <Text fontWeight={fontWeights.semiBold}>
              {notification.data.name}
            </Text>
            <Text fontWeight={fontWeights.normal}>
              {interpolateComponents({
                mixedString: sprintf(
                  notification.data.count > 1
                    ? getText(
                        '{{strong}}%(count)s{{/strong}} unread applications',
                      )
                    : getText(
                        '{{strong}}%(count)s{{/strong}} unread application',
                      ),
                  {
                    count: notification.data.count,
                  },
                ),
                components: {
                  strong: <strong />,
                },
              })}
            </Text>
          </>
        ),
      });

    case 'NEW_MENTIONS_BY_APPLICATION': {
      const title = truncateTitle(notification.data.listing.name);

      return render({
        to: `/admin/${notification.data.orgId}/applications/${notification.data.id}`,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.type,
          application_id: notification.data.id,
          listing_id: notification.data.listing.id,
          listing_type: notification.data.listing.type,
          listing_name: notification.data.listing.name,
          org_id: notification.data.orgId,
          mentions_count: notification.data.count,
        },
        children: interpolateComponents({
          mixedString: sprintf(
            notification.data.count === 1
              ? getText(
                  '%(name)s wants you to review an applicant for {{strong}}%(title)s{{/strong}}',
                )
              : getText(
                  'Your teammates want you to review an applicant for {{strong}}%(title)s{{/strong}}',
                ),
            {
              name: notification.data.mentionBy.name,
              title,
            },
          ),
          components: {
            strong: <strong />,
          },
        }),
      });
    }

    case 'NEW_SUGGESTED_APPLICANTS_BY_JOB': {
      const title = truncateTitle(notification.data.job.name);
      return render({
        to: `/admin/${notification.data.orgId}/jobs/${notification.data.job.id}/recommended-candidates`,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.type,
          listing_id: notification.data.job.id,
          listing_type: 'JOB',
          listing_name: notification.data.job.name,
          org_id: notification.data.orgId,
          candidate_count: notification.data.count,
        },
        children: (
          <>
            <Text fontWeight={fontWeights.semiBold}>{title}</Text>
            <Text fontWeight={fontWeights.normal}>
              {interpolateComponents({
                mixedString: sprintf(
                  getText(
                    '{{strong}}%(count)d{{/strong}} new recommended resumes',
                  ),
                  {
                    count: notification.data.count,
                  },
                ),
                components: {
                  strong: <strong />,
                },
              })}
            </Text>
          </>
        ),
      });
    }

    case 'ORG_ADMIN_USER_DROPDOWN_ATS_CTA': {
      return render({
        to: '/applicant-tracker',
        eventName: 'User Menu Notification',
        eventProperties: {},
        children: (
          <Text fontWeight={fontWeights.normal}>
            {interpolateComponents({
              mixedString: sprintf(
                getText(
                  'Try our applicant management tool, included with any listing. {{strong}}Learn More{{/strong}}',
                ),
              ),
              components: {
                strong: <strong />,
              },
            })}
          </Text>
        ),
      });
    }

    case 'SALARY_SURVEY_CTA': {
      return render({
        to: '/salaries',
        eventName: 'User Menu Notification',
        eventProperties: {
          cta_type: 'Link',
          button_text: 'Take Our Salary Survey',
          url: '/salaries',
          context: 'User Menu',
        },
        children: (
          <Text fontWeight={fontWeights.normal}>
            {getText('Take Our Salary Survey')}
          </Text>
        ),
      });
    }

    default:
      return null;
  }
}
